import React, { Suspense, useEffect, useState, useRef } from 'react'
import './assets/styles/App.scss'
import './assets/styles/custom.scss'
import { Route, Switch, Redirect, useLocation, matchPath } from 'react-router-dom'
import { isLoggedIn } from './utils/userUtils'
import generateRoutes, { fallbackRoutes } from './router'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from './redux/store'
import { processWebMenu } from './utils/menuUtils'
import { getListMenuFail, getListMenuSuccess } from './redux/ducks/menuDucks'
import Loadding from './components/layout/Loadding'
import BasicLayout from './components/layout/BasicLayout'
import * as userApi from './api/userApi';

const URL_LOGIN_ALL_SYSTEM = (process.env.REACT_APP_AUTH_URL || "http://localhost:3000") + '/login-all'

/** Giữ nguyên viết tắt; phần còn lại viết thường rồi hoa chữ đầu. */
const TITLE_ACRONYMS = ['HTVC', 'NCC', 'VAT', 'QR', 'VC', 'V2', 'VN', 'CN']

const formatPageTitle = (raw: string) => {
  let text = String(raw || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('vi')
  text = text.replace(/^cập nhật\b/, 'sửa')
  TITLE_ACRONYMS.forEach((acronym) => {
    text = text.replace(new RegExp(`\\b${acronym.toLocaleLowerCase('vi')}\\b`, 'gi'), acronym)
  })
  const first = text.search(/[^\s]/)
  if (first >= 0) {
    text = text.slice(0, first) + text.charAt(first).toLocaleUpperCase('vi') + text.slice(first + 1)
  }
  return `MH - ${text}`
}

const findRouteTitle = (routeList: any[], pathname: string) => {
  let best: { title: string; score: number } | null = null
  routeList.forEach((route) => {
    if (!route?.path || !route?.title) return
    const matched = matchPath(pathname, {
      path: route.path,
      exact: route.exact,
      strict: false,
    })
    if (!matched) return
    const score = String(route.path).length
    if (!best || score > best.score) best = { title: route.title, score }
  })
  return best?.title
}

const App: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch()
  const isMounted = useRef(true)
  const [isLoading, setIsLoading] = useState(true)
  const [routes, setRoutes] = useState<any[]>(fallbackRoutes)
  const isPublicPath =
    location.pathname === '/login' ||
    location.pathname === '/setting-token' ||
    location.pathname.includes('setting-token') ||
    location.pathname === '/privacy-policy' ||
    location.pathname === '/developer/contact'

  // Lấy menu data từ Redux store
  const { webChucNangAll, webUrls } = useSelector((state: RootState) => state.menu)

  useEffect(() => {
    const pageTitle = findRouteTitle(routes, location.pathname)
    document.title = pageTitle ? formatPageTitle(pageTitle) : 'Mua hàng'
  }, [location, routes]);

  useEffect(() => {
    isMounted.current = true
    if (isLoggedIn()) {
      fetchMyMenu()
    } else if (isMounted.current) {
      setIsLoading(false)
    }

    return () => {
      isMounted.current = false
    }
  }, [])

  // Cập nhật routes khi menu data thay đổi
  useEffect(() => {
    if (webChucNangAll && webChucNangAll.length > 0) {
      try {
        const updatedRoutes = generateRoutes(webChucNangAll)
        
        setRoutes(updatedRoutes)
      } catch (error) {
        setRoutes(fallbackRoutes)
      }
    }
  }, [webChucNangAll, webUrls, location.pathname])

  const fetchMyMenu = async () => {
    try {
      if (isMounted.current) setIsLoading(true)
      const res: any = await userApi.getMyMenu()

      if (isMounted.current && res.data && Array.isArray(res.data)) {
        const { webUrls, webChucNangs, webChucNangAll } = processWebMenu(res.data)
        
        dispatch(getListMenuSuccess({
          webUrls: webUrls,
          webChucNangs: webChucNangs,
          webChucNangAll: webChucNangAll
        }))
      }
    } catch (error) {
      if (isMounted.current) {
        dispatch(getListMenuFail({
          webUrls: [],
          webChucNangs: [],
          webChucNangAll: []
        }))
      }
    } finally {
      if (isMounted.current) setIsLoading(false)
    }
  }

  // Hiển thị loading khi đang fetch menu data (không chặn màn public/login)
  if (isLoading && !isPublicPath) {
    return <Loadding />
  }

  return (
    <Suspense fallback={<Loadding />}>
      <Switch>
        {routes.map(({ component: Component, path, exact, name, layout: Layout }) => (
          <Route
            key={name}
            exact={exact}
            path={path}
            render={(props: any) => {
              return (
                <Layout>
                  {isLoggedIn() ? (
                    path === '/login' ? (
                      <Redirect push to="/" />
                    ) : (
                      <Component {...props} />
                    )
                  ) : path === '/login' || path === '/privacy-policy' || path === '/developer/contact' ? (
                    <Component {...props} />
                  ) :
                    path.includes('setting-token') ? (
                      <Component {...props} />
                    ) : (
                      // <Redirect push to="/login" />
                      window.location.href = `${URL_LOGIN_ALL_SYSTEM}?hethong=muahang&redirect_url=${location.pathname}`
                    )}
                </Layout>
              )
            }}
          />
        ))}
        {/* Fallback route - chỉ render khi không có route nào match */}
        <Route path="*" render={() => {
          return (
            <BasicLayout>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <h2>404 - Trang không tồn tại</h2>
              </div>
            </BasicLayout>
          )
        }} />
      </Switch>
    </Suspense>
  )
}

export default App
